import { Injectable } from '@angular/core';
import { API } from './api';
import { BaseProvider } from './base';
import { OptimizeTag } from './boost';
import { BrainVersionType } from './brain';
import { TranslateService } from '@ngx-translate/core';

export enum Comprehensiveness {
    Quick = 'Quick', // resourceString('Quick'),
    Balanced = 'Medium', // resourceString('Balanced'),
    Thorough = 'Thorough' // resourceString('Thorough')
}

export enum DetectionOptimizationType {
    Fast = 'Fast', // resourceString('Fast'),
    Balanced = 'Balanced', // resourceString('Balanced'),
    Thorough = 'Thorough', // resourceString('Thorough')
    Test = 'Test' // resourceString('Test')
}

export enum OptimizeFor {
    Accuracy = 'Accuracy',
    AverageAccuracy = 'AverageAccuracy',
    LeastFP = 'LeastFP'
}

export enum OptimizeForDescriptions {
    Accuracy = 'Highest Accuracy', // resourceString('Highest Accuracy'),
    AverageAccuracy = 'Highest Average Accuracy', // resourceString('Highest Average Accuracy'),
    LeastFP = 'Least False Positives' // resourceString('Least False Positives')
}

export enum ModelSizing {
    OptimizeForDataset = 'OptimizeForDataset',
    Large = 'Large',
    Medium = 'Medium',
    Small = 'Small',
    Reuse = 'Reuse'
}

export interface OptimizationRun {
    optimizationRunIdentifier: string;
    comprehensiveness: Comprehensiveness;
    dateAdded: string;
    resultedInImprovement: boolean;
    initialAccuracy: number;
    initialTruePositives: number;
    initialFalsePositives: number;
    initialFalseNegatives: number;
    resultingAccuracy: number;
    resultingTruePositives: number;
    resultingFalsePositives: number;
    resultingFalseNegatives: number;
    runtime: number;
    adherenceToNormal: string;
    optimizeFor: string;
    tags: string[];
    tag: string;
}

const DEFAULT_UNKNOWN_TEXT = 'Unknown';

@Injectable()
export class OptimizationRunsProvider extends BaseProvider {
    constructor(private api: API, private translate: TranslateService) {
        super();
    }

    /**
     * Returns information about the latest optimization run
     * @param {string} datasetIdentifier Dataset's identifier
     */
    public async getLatestRun(
        datasetIdentifier: string,
        brainVersionType: BrainVersionType = BrainVersionType.Learning
    ): Promise<OptimizationRun> {
        const result = await this.debounce(
            `${datasetIdentifier}/optimization-runs/latest?brainVersionType=${brainVersionType}`,
            () => {
                return this.api.get(
                    `datasets/${datasetIdentifier}/optimization-runs/latest?brainVersionType=${brainVersionType}`
                );
            },
            250
        );

        return result.message as OptimizationRun;
    }

    public getComprehensivenessDescription(comprehensiveness: string): string {
        if (!comprehensiveness) {
            return DEFAULT_UNKNOWN_TEXT;
        } else if (comprehensiveness === Comprehensiveness.Balanced) {
            // This clause can eventually be removed once the backend stops returning "Medium" comprehensiveness.
            return this.translate.instant('Balanced');
        } else {
            return this.translate.instant(comprehensiveness);
        }
    }

    public getTagDescription(tag: string): string {
        switch (tag) {
            case OptimizeTag.Small:
                return this.translate.instant('Small');
            case OptimizeTag.Explainability:
                return this.translate.instant('Explainability Enabled');
            case OptimizeTag.Medium:
                return this.translate.instant('Medium');
            case OptimizeTag.Large:
                return this.translate.instant('Large');

            case OptimizeTag.HiFiUltraFine:
                return this.translate.instant('Ultrafine');
            case OptimizeTag.HiFiFine:
                return this.translate.instant('Fine');
            case OptimizeTag.HiFiRegular:
                return this.translate.instant('Regular');
            case OptimizeTag.HiFiCoarse:
                return this.translate.instant('Coarse');

            case OptimizeTag.Reuse:
                return this.translate.instant('Use same model architecture as previous brain version');

            default:
                return this.translate.instant('Optimize for dataset');
        }
    }
}



// WEBPACK FOOTER //
// ./src/providers/optimization-runs.ts